/**
 * ZO Bull Algo Trading Platform - React Frontend
 * Version: 6.2.0
 * Build: build-20260626
 */
import React, { useEffect, useState } from 'react'
import { BrowserRouter, Routes, Route } from 'react-router-dom'
import { ThemeProvider } from './contexts/ThemeContext'
import Layout from './components/Layout'
import Dashboard from './pages/Dashboard'
import Brokers from './pages/Brokers'
import Strategies from './pages/Strategies'
import Analytics from './pages/Analytics'
import NotificationsPage from './pages/NotificationsPage'
import Login from './pages/Login'
import Register from './pages/Register'
import { PWAInstallPrompt, PWAUpdateNotification, OfflineIndicator } from './components/PWAInstallPrompt'
import CopilotChat from './components/CopilotChat'
import CopilotPage from './pages/CopilotPage'
import OptionsAnalysis from './pages/OptionsAnalysis'
import PineScriptEditor from './pages/PineScriptEditor'
import TradetrONSettings from './pages/TradetrONSettings'
import Landing from './pages/Landing'
import CMSPages from './pages/CMSPages'
import Marketplace from './pages/Marketplace'
import Tutorials from './pages/Tutorials'
import CreatorProfile from './pages/CreatorProfile'
import Wishlist from './pages/Wishlist'
import Compare from './pages/Compare'
import StrategyPerformance from './pages/StrategyPerformance'
import KeyboardShortcuts from './components/KeyboardShortcuts'
import { ToastProvider } from './components/Toast'
import { SnackbarProvider } from './components/Snackbar'
import LanguageDetectionNotice from './components/LanguageDetectionNotice'
import FloatingLanguageSwitcher from './components/FloatingLanguageSwitcher'
import ProtectedRoute from './components/ProtectedRoute'
import AuthGuard from './components/AuthGuard'
import AdminLogin from './pages/AdminLogin'
import AdminPanel from './pages/AdminPanel'

// Error Boundary HOC
function withErrorBoundary<P extends object>(
  Component: React.ComponentType<P>,
  fallbackName: string
) {
  return function WrappedComponent(props: P) {
    return (
      <ErrorBoundaryComponent name={fallbackName}>
        <Component {...props} />
      </ErrorBoundaryComponent>
    )
  }
}

// Error Boundary Component
class ErrorBoundaryComponent extends React.Component<
  { children: React.ReactNode; name?: string },
  { hasError: boolean; error: Error | null }
> {
  constructor(props: { children: React.ReactNode; name?: string }) {
    super(props)
    this.state = { hasError: false, error: null }
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error }
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error(`[${this.props.name || 'Component'}] Error:`, error.message)
    console.error('Stack:', errorInfo.componentStack)
  }

  render() {
    if (this.state.hasError) {
      // Return a visible error for debugging
      return (
        <div style={{
          padding: '20px',
          color: '#ff4444',
          background: '#1a1a2e',
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          <div style={{maxWidth: '600px', textAlign: 'center'}}>
            <h2>⚠️ Error: {this.props.name || 'Component'}</h2>
            <pre style={{textAlign: 'left', background: '#000', padding: '20px', borderRadius: '8px', overflow: 'auto', fontSize: '12px'}}>
              {this.state.error?.message}
              {'\n\n'}
              {this.state.error?.stack}
            </pre>
            <button onClick={() => window.location.reload()} style={{marginTop: '20px', padding: '10px 20px', cursor: 'pointer'}}>
              Reload Page
            </button>
          </div>
        </div>
      )
    }
    return this.props.children
  }
}

function App() {
  const [showShortcuts, setShowShortcuts] = useState(false)
  
  console.log('[App] Rendering...')

  // Global error handler
  useEffect(() => {
    const handleError = (event: ErrorEvent) => {
      console.error('[Global Error]', event.error)
    }
    const handleRejection = (event: PromiseRejectionEvent) => {
      console.error('[Unhandled Rejection]', event.reason)
    }
    window.addEventListener('error', handleError)
    window.addEventListener('unhandledrejection', handleRejection)
    return () => {
      window.removeEventListener('error', handleError)
      window.removeEventListener('unhandledrejection', handleRejection)
    }
  }, [])
  
  useEffect(() => {
    const savedTheme = localStorage.getItem('zobull-theme')
    if (savedTheme) {
      document.documentElement.setAttribute('data-theme', savedTheme)
    }
  }, [])

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement
      if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable) {
        return
      }
      const key = e.key.toLowerCase()
      if (key === '?' || (e.shiftKey && key === '/')) {
        e.preventDefault()
        setShowShortcuts(prev => !prev)
      }
      if (key === 'escape' && showShortcuts) {
        setShowShortcuts(false)
      }
    }
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [showShortcuts])

  return (
    <ThemeProvider>
      <ToastProvider>
        <SnackbarProvider>
          <BrowserRouter>
            <Routes>
              {/* Public Routes */}
              <Route path="/login" element={<Login />} />
              <Route path="/register" element={<Register />} />
              <Route path="/landing" element={<Landing />} />
              
              {/* CMS Pages - Public */}
              <Route path="/about" element={<CMSPages page="about" />} />
              <Route path="/contact" element={<CMSPages page="contact" />} />
              <Route path="/terms" element={<CMSPages page="terms" />} />
              <Route path="/privacy" element={<CMSPages page="privacy" />} />
              <Route path="/disclaimer" element={<CMSPages page="disclaimer" />} />
              <Route path="/refund" element={<CMSPages page="refund" />} />
              <Route path="/howto" element={<CMSPages page="howto" />} />
              <Route path="/faq" element={<CMSPages page="faq" />} />
              
              {/* Admin Routes */}
              <Route path="/admin/login" element={<AdminLogin />} />
              <Route path="/admin/dashboard" element={<AdminPanel />} />
              <Route path="/admin" element={<AdminLogin />} />
              
              {/* Root route - Landing page for all users */}
              <Route path="/" element={<Landing />} />
              
              {/* Protected Routes - AuthGuard wraps Layout for consistent auth + nav */}
              <Route element={<AuthGuard />}>
                <Route element={<Layout />}>
                  <Route path="/dashboard" element={<Dashboard />} />
                  <Route path="brokers" element={<Brokers />} />
                  <Route path="strategies" element={<Strategies />} />
                  <Route path="analytics" element={<Analytics />} />
                  <Route path="notifications" element={<NotificationsPage />} />
                  <Route path="copilot" element={<CopilotPage />} />
                  <Route path="tutorials" element={<Tutorials />} />
                  <Route path="options-analysis" element={<OptionsAnalysis />} />
                  <Route path="pine-script-editor" element={<PineScriptEditor />} />
                  <Route path="tradetron-settings" element={<TradetrONSettings />} />
                  <Route path="marketplace" element={<Marketplace />} />
                  <Route path="creator/:userId" element={<CreatorProfile />} />
                  <Route path="wishlist" element={<Wishlist />} />
                  <Route path="compare" element={<Compare />} />
                  <Route path="strategy/:strategyId/performance" element={<StrategyPerformance />} />
                </Route>
              </Route>
            </Routes>
            
            {/* Wrapped Components with Error Boundaries */}
            <ErrorBoundaryComponent name="PWAInstallPrompt">
              <PWAInstallPrompt />
            </ErrorBoundaryComponent>
            <ErrorBoundaryComponent name="PWAUpdateNotification">
              <PWAUpdateNotification />
            </ErrorBoundaryComponent>
            <ErrorBoundaryComponent name="OfflineIndicator">
              <OfflineIndicator />
            </ErrorBoundaryComponent>
            <ErrorBoundaryComponent name="CopilotChat">
              <CopilotChat />
            </ErrorBoundaryComponent>
            <ErrorBoundaryComponent name="KeyboardShortcuts">
              <KeyboardShortcuts 
                isOpen={showShortcuts} 
                onClose={() => setShowShortcuts(false)} 
              />
            </ErrorBoundaryComponent>
            <ErrorBoundaryComponent name="LanguageDetectionNotice">
              <LanguageDetectionNotice />
            </ErrorBoundaryComponent>
            <ErrorBoundaryComponent name="FloatingLanguageSwitcher">
              <FloatingLanguageSwitcher />
            </ErrorBoundaryComponent>
          </BrowserRouter>
        </SnackbarProvider>
      </ToastProvider>
    </ThemeProvider>
  )
}

export default App